import { FormEvent, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Datepicker,
  type MbscDatepickerChangeEvent,
} from '@akoom-tech/mobiscroll-react';
import {
  Avatar,
  Box,
  Button,
  Checkbox,
  Divider,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import DialogSimple from '@/components/dialog/DialogSimple';
import { Iconify } from '@/components';
import { getSettingsData } from '@/features/app/api';
import {
  deleteBookingSession,
  updateBookingSessionsBulk,
} from '@/features/bookings-management/api';
import { BookingSession } from '@/features/bookings-management/types';
import {
  bookingTimePickerOptions,
  isVoidBookingSessionStatus,
  showClientCleanerRestrictionWarnings,
  timePickerSx,
} from '@/features/bookings-management/utils';
import { verifyCoupon } from '@/features/coupons/api';
import { getActiveProviders } from '@/features/users/shared/api';
import { User } from '@/features/users/shared/types';
import { useSnackbarMsg } from '@/hooks/useSnackbarMsg';

type BulkActionMode =
  | 'statuses'
  | 'time'
  | 'cleaner'
  | 'providerRate'
  | 'linenAmenities'
  | 'coupon'
  | 'delete'
  | null;

type BulkUpdateRequest = {
  sessionIds: number[];
  data: Partial<BookingSession>;
};
type LinenAmenitiesBulkData = Partial<
  Pick<BookingSession, 'linen' | 'essential_amenities'>
>;

type BulkUpdateRequestInput =
  | BulkUpdateRequest[]
  | (() => Promise<BulkUpdateRequest[]>);

type VBMBookingSessionsBulkActionsProps = {
  selectedSessions: BookingSession[];
  onClearSelection: VoidFunction;
  onSelectionChange: (selectedIds: number[]) => void;
  visibleSessionCount?: number;
  onSelectAllSessions?: (checked: boolean) => void;
  canUpdate?: boolean;
  canDelete?: boolean;
  onSessionsDeleted?: (deletedIds: number[]) => void;
};

const UNCHANGED_VALUE = '';
const UNASSIGNED_CLEANER_VALUE = '0';
const BOOLEAN_TRUE_VALUE = 'true';
const BOOLEAN_FALSE_VALUE = 'false';
const SESSION_TIME_PATTERN = /^([01]\d|2[0-3]):[0-5]\d$/;

export default function VBMBookingSessionsBulkActions({
  selectedSessions,
  onClearSelection,
  onSelectionChange,
  visibleSessionCount,
  onSelectAllSessions,
  canUpdate = true,
  canDelete = false,
  onSessionsDeleted,
}: VBMBookingSessionsBulkActionsProps) {
  const theme = useTheme();
  const queryClient = useQueryClient();
  const { successMsg, errorMsg, warningMsg } = useSnackbarMsg();
  const [activeMode, setActiveMode] = useState<BulkActionMode>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [statusId, setStatusId] = useState(UNCHANGED_VALUE);
  const [paymentStatusId, setPaymentStatusId] = useState(UNCHANGED_VALUE);
  const [sessionTime, setSessionTime] = useState('');
  const [cleanerId, setCleanerId] = useState(UNCHANGED_VALUE);
  const [providerRateInput, setProviderRateInput] = useState('');
  const [providerRateErrorText, setProviderRateErrorText] = useState('');
  const [linenValue, setLinenValue] = useState(UNCHANGED_VALUE);
  const [essentialAmenitiesValue, setEssentialAmenitiesValue] =
    useState(UNCHANGED_VALUE);
  const [couponCode, setCouponCode] = useState('');

  const selectedCount = selectedSessions.length;
  const rowCount = visibleSessionCount ?? selectedCount;
  const selectedDeletableSessions = useMemo(
    () =>
      selectedSessions.filter((session) =>
        isVoidBookingSessionStatus(session.booking_session_status)
      ),
    [selectedSessions]
  );
  const selectedNonDeletableSessionIds = useMemo(
    () =>
      selectedSessions
        .filter(
          (session) =>
            !isVoidBookingSessionStatus(session.booking_session_status)
        )
        .map(({ id }) => id),
    [selectedSessions]
  );
  const selectedDeletableCount = selectedDeletableSessions.length;
  const skippedDeleteCount = selectedCount - selectedDeletableCount;
  const defaultSessionTime = useMemo(
    () =>
      selectedSessions.find(
        (session) =>
          typeof session.time === 'string' &&
          SESSION_TIME_PATTERN.test(session.time)
      )?.time ?? '',
    [selectedSessions]
  );

  const { data: settingsData, isLoading: isSettingsLoading } = useQuery({
    queryKey: ['settings'],
    queryFn: getSettingsData,
    enabled: activeMode === 'statuses',
    onError: (error) => {
      errorMsg(error, 'Something went wrong while fetching statuses');
    },
  });

  const { data: activeProviders, isLoading: isProvidersLoading } = useQuery({
    queryKey: ['providers', 'active'],
    queryFn: getActiveProviders,
    enabled: activeMode === 'cleaner',
    onError: (error) => {
      errorMsg(error, 'Something went wrong while fetching providers');
    },
  });

  const providerOptions = useMemo(
    () => [
      {
        id: 0,
        first_name: 'Unassigned',
        last_name: 'Cleaner',
        profile_image: '',
      },
      ...((activeProviders ?? []) as User[]),
    ],
    [activeProviders]
  );

  const handleCloseDialog = () => {
    if (isSubmitting) return;
    setActiveMode(null);
  };

  const resetDialogState = () => {
    setStatusId(UNCHANGED_VALUE);
    setPaymentStatusId(UNCHANGED_VALUE);
    setSessionTime('');
    setCleanerId(UNCHANGED_VALUE);
    setProviderRateInput('');
    setProviderRateErrorText('');
    setLinenValue(UNCHANGED_VALUE);
    setEssentialAmenitiesValue(UNCHANGED_VALUE);
    setCouponCode('');
  };

  const openMode = (mode: BulkActionMode) => {
    resetDialogState();
    if (mode === 'time') {
      setSessionTime(defaultSessionTime);
    }
    setActiveMode(mode);
  };

  const invalidateAffectedQueries = (affectedSessions: BookingSession[]) => {
    queryClient.invalidateQueries(['bookingSessions']);
    affectedSessions.forEach((session) => {
      queryClient.invalidateQueries(['bookingSession', String(session.id)]);
    });
  };

  const invalidateDeletedSessionQueries = (deletedSessionIds: number[]) => {
    queryClient.invalidateQueries(['bookingSessions']);
    queryClient.invalidateQueries(['bookings']);
    deletedSessionIds.forEach((sessionId) => {
      queryClient.removeQueries(['bookingSession', String(sessionId)]);
    });
  };

  const runBulkOperation = async (
    actionLabel: string,
    requestInput: BulkUpdateRequestInput
  ) => {
    const sessionsToUpdate = [...selectedSessions];

    setIsSubmitting(true);

    try {
      const requests =
        typeof requestInput === 'function'
          ? await requestInput()
          : requestInput;
      const results = await Promise.allSettled(
        requests.map(async (request) => {
          const result = await updateBookingSessionsBulk(
            request.sessionIds,
            request.data
          );

          return {
            sessionIds: request.sessionIds,
            failedSessionIds: result.failed_sessions.map(({ id }) => id),
            warnings: result.warnings ?? [],
          };
        })
      );
      const failedSessionIds = Array.from(
        new Set(
          results.flatMap((result, index) => {
            if (result.status === 'rejected') {
              return requests[index].sessionIds;
            }

            return result.value.failedSessionIds;
          })
        )
      );
      const succeededCount = sessionsToUpdate.length - failedSessionIds.length;
      const warnings = results.flatMap((result) =>
        result.status === 'fulfilled' ? result.value.warnings : []
      );

      invalidateAffectedQueries(sessionsToUpdate);

      if (failedSessionIds.length === 0) {
        successMsg(
          `${actionLabel} applied to ${sessionsToUpdate.length} ${
            sessionsToUpdate.length === 1 ? 'session' : 'sessions'
          }`
        );
        onClearSelection();
      } else {
        onSelectionChange(failedSessionIds);
        errorMsg(
          `Failed sessions: ${failedSessionIds
            .map((sessionId) => `#${sessionId}`)
            .join(', ')}`,
          `${actionLabel} partially completed (${succeededCount}/${sessionsToUpdate.length})`
        );
      }

      showClientCleanerRestrictionWarnings(
        { warnings },
        warningMsg,
        activeProviders
      );
      setActiveMode(null);
    } catch (error) {
      errorMsg(error, `${actionLabel} failed`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmitStatuses = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!statusId && !paymentStatusId) {
      errorMsg('Choose at least one status to update', 'Bulk statuses blocked');
      return;
    }

    void runBulkOperation('Bulk status update', [
      {
        sessionIds: selectedSessions.map(({ id }) => id),
        data: {
          ...(statusId ? { status_id: Number(statusId) } : {}),
          ...(paymentStatusId
            ? { payment_status_id: Number(paymentStatusId) }
            : {}),
        },
      },
    ]);
  };

  const handleSubmitCleaner = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (cleanerId === UNCHANGED_VALUE) {
      errorMsg('Choose a cleaner or unassigned', 'Bulk cleaner update blocked');
      return;
    }

    const nextCleanerId = Number(cleanerId);

    void runBulkOperation('Bulk cleaner update', [
      {
        sessionIds: selectedSessions.map(({ id }) => id),
        data: {
          booking_cleaners:
            nextCleanerId === Number(UNASSIGNED_CLEANER_VALUE)
              ? []
              : [nextCleanerId],
        },
      },
    ]);
  };

  const handleSubmitTime = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!SESSION_TIME_PATTERN.test(sessionTime)) {
      errorMsg(
        'Enter a valid time in HH:mm format',
        'Bulk time update blocked'
      );
      return;
    }

    void runBulkOperation('Bulk time update', [
      {
        sessionIds: selectedSessions.map(({ id }) => id),
        data: {
          time: sessionTime,
        },
      },
    ]);
  };

  const handleSessionTimeChange = (event: MbscDatepickerChangeEvent) => {
    setSessionTime(String(event.valueText ?? ''));
  };

  const handleSubmitProviderRate = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const trimmedValue = providerRateInput.trim();

    if (!trimmedValue) {
      setProviderRateErrorText('Enter a provider rate.');
      return;
    }

    const parsedRate = Number(trimmedValue);

    if (!Number.isFinite(parsedRate)) {
      setProviderRateErrorText('Enter a valid provider rate.');
      return;
    }

    if (parsedRate < 0) {
      setProviderRateErrorText('Provider rate cannot be negative.');
      return;
    }

    void runBulkOperation('Bulk provider rate update', [
      {
        sessionIds: selectedSessions.map(({ id }) => id),
        data: {
          provider_price_per_hour: parsedRate,
        },
      },
    ]);
  };

  const handleSubmitLinenAmenities = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (
      linenValue === UNCHANGED_VALUE &&
      essentialAmenitiesValue === UNCHANGED_VALUE
    ) {
      errorMsg(
        'Choose linen or essential amenities to update',
        'Bulk linen and amenities blocked'
      );
      return;
    }

    const data: LinenAmenitiesBulkData = {
      ...(linenValue !== UNCHANGED_VALUE
        ? { linen: linenValue === BOOLEAN_TRUE_VALUE }
        : {}),
      ...(essentialAmenitiesValue !== UNCHANGED_VALUE
        ? {
            essential_amenities: essentialAmenitiesValue === BOOLEAN_TRUE_VALUE,
          }
        : {}),
    };

    void runBulkOperation('Bulk linen and amenities update', [
      {
        sessionIds: selectedSessions.map(({ id }) => id),
        data,
      },
    ]);
  };

  const handleSubmitCoupon = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const trimmedCouponCode = couponCode.trim();

    if (!trimmedCouponCode) {
      errorMsg('Coupon code required', 'Bulk coupon blocked');
      return;
    }

    const sessionsMissingVariant = selectedSessions.filter(
      (session) => !session.service_variant?.id
    );

    if (sessionsMissingVariant.length > 0) {
      errorMsg(
        `Sessions missing service variant: ${sessionsMissingVariant
          .map((session) => `#${session.id}`)
          .join(', ')}`,
        'Bulk coupon blocked'
      );
      return;
    }

    const couponByServiceVariant = new Map<number, Promise<number>>();

    const getCouponId = (serviceVariantId: number) => {
      if (!couponByServiceVariant.has(serviceVariantId)) {
        couponByServiceVariant.set(
          serviceVariantId,
          verifyCoupon({
            service_variant_id: serviceVariantId,
            couponCode: trimmedCouponCode,
            platform: 'WEB_PLATFORM',
          }).then((coupon) => coupon.id)
        );
      }

      return couponByServiceVariant.get(serviceVariantId) as Promise<number>;
    };

    void runBulkOperation('Bulk coupon update', async () => {
      const sessionIdsByCouponId = new Map<number, number[]>();
      const sessionCouponPairs = await Promise.all(
        selectedSessions.map(async (session) => ({
          sessionId: session.id,
          couponId: await getCouponId(session.service_variant.id),
        }))
      );

      sessionCouponPairs.forEach(({ sessionId, couponId }) => {
        sessionIdsByCouponId.set(couponId, [
          ...(sessionIdsByCouponId.get(couponId) ?? []),
          sessionId,
        ]);
      });

      return Array.from(sessionIdsByCouponId.entries()).map(
        ([couponId, sessionIds]) => ({
          sessionIds,
          data: { coupon_code_id: couponId },
        })
      );
    });
  };

  const handleConfirmDelete = async () => {
    if (!canDelete || selectedDeletableSessions.length === 0) {
      errorMsg('Only void sessions can be deleted', 'Bulk delete blocked');
      return;
    }

    const sessionsToDelete = [...selectedDeletableSessions];

    setIsSubmitting(true);

    try {
      const results = await Promise.allSettled(
        sessionsToDelete.map((session) =>
          deleteBookingSession(session.id).then(() => session.id)
        )
      );
      const failedSessionIds = results.flatMap((result, index) =>
        result.status === 'rejected' ? [sessionsToDelete[index].id] : []
      );
      const failedSessionIdsSet = new Set(failedSessionIds);
      const succeededSessionIds = sessionsToDelete
        .map(({ id }) => id)
        .filter((id) => !failedSessionIdsSet.has(id));
      const remainingSelectedIds = [
        ...selectedNonDeletableSessionIds,
        ...failedSessionIds,
      ];

      invalidateDeletedSessionQueries(succeededSessionIds);
      if (succeededSessionIds.length > 0) {
        onSessionsDeleted?.(succeededSessionIds);
      }

      if (failedSessionIds.length === 0) {
        successMsg(
          `Deleted ${succeededSessionIds.length} ${
            succeededSessionIds.length === 1 ? 'session' : 'sessions'
          }${
            skippedDeleteCount
              ? `. Skipped ${skippedDeleteCount} selected ${
                  skippedDeleteCount === 1 ? 'session' : 'sessions'
                } because only void sessions can be deleted.`
              : ''
          }`
        );

        if (remainingSelectedIds.length > 0) {
          onSelectionChange(remainingSelectedIds);
        } else {
          onClearSelection();
        }
      } else {
        onSelectionChange(remainingSelectedIds);
        errorMsg(
          `Failed sessions: ${failedSessionIds
            .map((sessionId) => `#${sessionId}`)
            .join(', ')}`,
          `Bulk delete partially completed (${succeededSessionIds.length}/${sessionsToDelete.length})`
        );
      }

      setActiveMode(null);
    } catch (error) {
      errorMsg(error, 'Bulk delete failed');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        alignItems={{ xs: 'stretch', sm: 'center' }}
        spacing={1}
        sx={{
          p: 1.25,
          mb: 1.5,
          borderRadius: 1,
          bgcolor: 'primary.lighter',
        }}
      >
        <Stack direction="row" alignItems="center" sx={{ flexGrow: 1 }}>
          {onSelectAllSessions && (
            <Checkbox
              indeterminate={selectedCount > 0 && selectedCount < rowCount}
              checked={rowCount > 0 && selectedCount === rowCount}
              disableRipple
              onChange={(event) => onSelectAllSessions(event.target.checked)}
              inputProps={{ 'aria-label': 'Select all visible sessions' }}
              sx={{ mr: 1 }}
            />
          )}
          <Typography variant="subtitle1" color="primary.main">
            {selectedCount} {selectedCount === 1 ? 'session' : 'sessions'}{' '}
            selected
          </Typography>
        </Stack>

        <Stack direction="row" justifyContent="flex-end" spacing={0.5}>
          {canUpdate && (
            <>
              <Tooltip title="Update statuses">
                <span>
                  <IconButton
                    color="primary"
                    aria-label="Update selected session statuses"
                    onClick={() => openMode('statuses')}
                    disabled={selectedCount === 0}
                  >
                    <Iconify icon="eva:checkmark-square-2-outline" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Change time">
                <span>
                  <IconButton
                    color="primary"
                    aria-label="Change time for selected sessions"
                    onClick={() => openMode('time')}
                    disabled={selectedCount === 0}
                  >
                    <Iconify icon="eva:clock-outline" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Assign cleaner">
                <span>
                  <IconButton
                    color="info"
                    aria-label="Assign cleaner to selected sessions"
                    onClick={() => openMode('cleaner')}
                    disabled={selectedCount === 0}
                  >
                    <Iconify icon="eva:person-add-outline" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Change provider rate">
                <span>
                  <IconButton
                    color="warning"
                    aria-label="Change provider rate for selected sessions"
                    onClick={() => openMode('providerRate')}
                    disabled={selectedCount === 0}
                  >
                    <Iconify icon="mdi:cash-clock" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Update linen and amenities">
                <span>
                  <IconButton
                    color="info"
                    aria-label="Update linen and amenities for selected sessions"
                    onClick={() => openMode('linenAmenities')}
                    disabled={selectedCount === 0}
                  >
                    <Iconify icon="openmoji:bed-linen" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Apply coupon">
                <span>
                  <IconButton
                    color="secondary"
                    aria-label="Apply coupon to selected sessions"
                    onClick={() => openMode('coupon')}
                    disabled={selectedCount === 0}
                  >
                    <Iconify icon="iconoir:percentage" />
                  </IconButton>
                </span>
              </Tooltip>
            </>
          )}
          {canDelete && (
            <Tooltip
              title={
                selectedDeletableCount > 0
                  ? 'Delete selected void sessions'
                  : 'Only void sessions can be deleted'
              }
            >
              <span>
                <IconButton
                  color="error"
                  aria-label="Delete selected sessions"
                  onClick={() => openMode('delete')}
                  disabled={selectedDeletableCount === 0}
                >
                  <Iconify icon="eva:trash-2-outline" />
                </IconButton>
              </span>
            </Tooltip>
          )}
          <Divider flexItem orientation="vertical" sx={{ mx: 0.5 }} />
          <Tooltip title="Clear selection">
            <IconButton
              color="inherit"
              aria-label="Clear selected sessions"
              onClick={onClearSelection}
            >
              <Iconify icon="eva:close-outline" />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>

      <DialogSimple
        dialogTitle={`Update ${selectedCount} selected ${
          selectedCount === 1 ? 'session' : 'sessions'
        }`}
        open={activeMode === 'statuses'}
        loadingButton={false}
        disagreeButton={false}
        onClickButtonClose={handleCloseDialog}
      >
        <form onSubmit={handleSubmitStatuses}>
          <Stack spacing={2} sx={{ px: 3, pt: 1, pb: 2 }}>
            <TextField
              select
              fullWidth
              size="small"
              label="Booking status"
              value={statusId}
              onChange={(event) => setStatusId(event.target.value)}
              disabled={isSettingsLoading || isSubmitting}
            >
              <MenuItem value={UNCHANGED_VALUE}>Leave unchanged</MenuItem>
              {(settingsData?.bookingSessionStatuses ?? []).map((status) => (
                <MenuItem key={status.id} value={String(status.id)}>
                  {status.display}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              fullWidth
              size="small"
              label="Payment status"
              value={paymentStatusId}
              onChange={(event) => setPaymentStatusId(event.target.value)}
              disabled={isSettingsLoading || isSubmitting}
            >
              <MenuItem value={UNCHANGED_VALUE}>Leave unchanged</MenuItem>
              {(settingsData?.bookingPaymentStatuses ?? []).map((status) => (
                <MenuItem key={status.id} value={String(status.id)}>
                  {status.display}
                </MenuItem>
              ))}
            </TextField>
            <Stack direction="row" justifyContent="flex-end" spacing={1}>
              <Button
                variant="outlined"
                size="small"
                color="inherit"
                onClick={handleCloseDialog}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <LoadingButton
                variant="contained"
                size="small"
                type="submit"
                loading={isSubmitting}
                disabled={isSettingsLoading}
              >
                Update statuses
              </LoadingButton>
            </Stack>
          </Stack>
        </form>
      </DialogSimple>

      <DialogSimple
        dialogTitle={`Change time for ${selectedCount} selected ${
          selectedCount === 1 ? 'session' : 'sessions'
        }`}
        open={activeMode === 'time'}
        loadingButton={false}
        disagreeButton={false}
        onClickButtonClose={handleCloseDialog}
      >
        <form onSubmit={handleSubmitTime}>
          <Stack spacing={2} sx={{ px: 3, pt: 1, pb: 2 }}>
            <Box sx={timePickerSx(theme)}>
              <Datepicker
                name="VBMBookingSessionsBulkActions-mbscTimePicker"
                theme="ios"
                themeVariant="light"
                label="Time"
                value={sessionTime}
                onChange={handleSessionTimeChange}
                controls={bookingTimePickerOptions.controls}
                display="anchored"
                touchUi={true}
                stepMinute={bookingTimePickerOptions.stepMinute}
                animation="pop"
                timeFormat={bookingTimePickerOptions.timeFormat}
                disabled={isSubmitting}
              />
            </Box>
            <Stack direction="row" justifyContent="flex-end" spacing={1}>
              <Button
                variant="outlined"
                size="small"
                color="inherit"
                onClick={handleCloseDialog}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <LoadingButton
                variant="contained"
                size="small"
                type="submit"
                loading={isSubmitting}
              >
                Update time
              </LoadingButton>
            </Stack>
          </Stack>
        </form>
      </DialogSimple>

      <DialogSimple
        dialogTitle={`Assign cleaner to ${selectedCount} selected ${
          selectedCount === 1 ? 'session' : 'sessions'
        }`}
        open={activeMode === 'cleaner'}
        loadingButton={false}
        disagreeButton={false}
        onClickButtonClose={handleCloseDialog}
      >
        <form onSubmit={handleSubmitCleaner}>
          <Stack spacing={2} sx={{ px: 3, pt: 1, pb: 2 }}>
            <TextField
              select
              fullWidth
              size="small"
              label="Cleaner"
              value={cleanerId}
              onChange={(event) => setCleanerId(event.target.value)}
              disabled={isProvidersLoading || isSubmitting}
            >
              <MenuItem value={UNCHANGED_VALUE} disabled>
                Choose cleaner
              </MenuItem>
              {providerOptions.map((provider) => (
                <MenuItem key={provider.id} value={String(provider.id)}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    {provider.id !== 0 && (
                      <Avatar
                        src={provider.profile_image}
                        alt={`${provider.first_name ?? 'N/A'} ${
                          provider.last_name ?? 'N/A'
                        }`}
                        sx={{ width: 24, height: 24 }}
                      />
                    )}
                    <Box>
                      {provider.first_name ?? 'N/A'}{' '}
                      {provider.last_name ?? 'N/A'}
                    </Box>
                  </Stack>
                </MenuItem>
              ))}
            </TextField>
            <Stack direction="row" justifyContent="flex-end" spacing={1}>
              <Button
                variant="outlined"
                size="small"
                color="inherit"
                onClick={handleCloseDialog}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <LoadingButton
                variant="contained"
                size="small"
                type="submit"
                loading={isSubmitting}
                disabled={isProvidersLoading}
              >
                Save cleaner
              </LoadingButton>
            </Stack>
          </Stack>
        </form>
      </DialogSimple>

      <DialogSimple
        dialogTitle={`Change provider rate for ${selectedCount} selected ${
          selectedCount === 1 ? 'session' : 'sessions'
        }`}
        open={activeMode === 'providerRate'}
        loadingButton={false}
        disagreeButton={false}
        onClickButtonClose={handleCloseDialog}
      >
        <form onSubmit={handleSubmitProviderRate}>
          <Stack spacing={2} sx={{ px: 3, pt: 1, pb: 2 }}>
            <TextField
              fullWidth
              size="small"
              label="Provider rate per hour"
              type="text"
              value={providerRateInput}
              onChange={(event) => {
                setProviderRateInput(event.target.value);
                setProviderRateErrorText('');
              }}
              disabled={isSubmitting}
              autoFocus
              inputProps={{ inputMode: 'decimal' }}
              error={Boolean(providerRateErrorText)}
              helperText={
                providerRateErrorText ||
                "Updates the selected sessions' provider rate."
              }
            />
            <Stack direction="row" justifyContent="flex-end" spacing={1}>
              <Button
                variant="outlined"
                size="small"
                color="inherit"
                onClick={handleCloseDialog}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <LoadingButton
                variant="contained"
                size="small"
                type="submit"
                loading={isSubmitting}
              >
                Save provider rate
              </LoadingButton>
            </Stack>
          </Stack>
        </form>
      </DialogSimple>

      <DialogSimple
        dialogTitle={`Update linen and amenities for ${selectedCount} selected ${
          selectedCount === 1 ? 'session' : 'sessions'
        }`}
        open={activeMode === 'linenAmenities'}
        loadingButton={false}
        disagreeButton={false}
        onClickButtonClose={handleCloseDialog}
      >
        <form onSubmit={handleSubmitLinenAmenities}>
          <Stack spacing={2} sx={{ px: 3, pt: 1, pb: 2 }}>
            <TextField
              select
              fullWidth
              size="small"
              label="Linen"
              value={linenValue}
              onChange={(event) => setLinenValue(event.target.value)}
              disabled={isSubmitting}
            >
              <MenuItem value={UNCHANGED_VALUE}>Leave unchanged</MenuItem>
              <MenuItem value={BOOLEAN_TRUE_VALUE}>Include linen</MenuItem>
              <MenuItem value={BOOLEAN_FALSE_VALUE}>No linen</MenuItem>
            </TextField>
            <TextField
              select
              fullWidth
              size="small"
              label="Essential Amenities"
              value={essentialAmenitiesValue}
              onChange={(event) =>
                setEssentialAmenitiesValue(event.target.value)
              }
              disabled={isSubmitting}
            >
              <MenuItem value={UNCHANGED_VALUE}>Leave unchanged</MenuItem>
              <MenuItem value={BOOLEAN_TRUE_VALUE}>
                Include essential amenities
              </MenuItem>
              <MenuItem value={BOOLEAN_FALSE_VALUE}>
                No essential amenities
              </MenuItem>
            </TextField>
            <Stack direction="row" justifyContent="flex-end" spacing={1}>
              <Button
                variant="outlined"
                size="small"
                color="inherit"
                onClick={handleCloseDialog}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <LoadingButton
                variant="contained"
                size="small"
                type="submit"
                loading={isSubmitting}
              >
                Save linen and amenities
              </LoadingButton>
            </Stack>
          </Stack>
        </form>
      </DialogSimple>

      <DialogSimple
        dialogTitle={`Apply coupon to ${selectedCount} selected ${
          selectedCount === 1 ? 'session' : 'sessions'
        }`}
        open={activeMode === 'coupon'}
        loadingButton={false}
        disagreeButton={false}
        onClickButtonClose={handleCloseDialog}
      >
        <form onSubmit={handleSubmitCoupon}>
          <Stack spacing={2} sx={{ px: 3, pt: 1, pb: 2 }}>
            <TextField
              fullWidth
              size="small"
              label="Coupon code"
              value={couponCode}
              onChange={(event) => setCouponCode(event.target.value)}
              disabled={isSubmitting}
              autoFocus
            />
            <Stack direction="row" justifyContent="flex-end" spacing={1}>
              <Button
                variant="outlined"
                size="small"
                color="inherit"
                onClick={handleCloseDialog}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <LoadingButton
                variant="contained"
                size="small"
                type="submit"
                loading={isSubmitting}
              >
                Apply coupon
              </LoadingButton>
            </Stack>
          </Stack>
        </form>
      </DialogSimple>

      <DialogSimple
        dialogTitle={`Delete ${selectedDeletableCount} selected ${
          selectedDeletableCount === 1 ? 'session' : 'sessions'
        }?`}
        open={activeMode === 'delete'}
        onClickButtonClose={handleCloseDialog}
        onClickButtonLoading={handleConfirmDelete}
        loadingButton
        loadingButtonText="Delete"
        loadingButtonColor="error"
        disableLoadingButton={selectedDeletableCount === 0}
        disagreeButton
        disagreeButtonText="Cancel"
        disagreeButtonColor="inherit"
        isSubmitting={isSubmitting}
      >
        <Stack spacing={1} sx={{ px: 3, pb: 2 }}>
          <Typography>
            This will permanently delete {selectedDeletableCount}{' '}
            {selectedDeletableCount === 1 ? 'void session' : 'void sessions'}.
            This action cannot be undone.
          </Typography>
          {skippedDeleteCount > 0 && (
            <Typography variant="body2" color="text.secondary">
              {skippedDeleteCount} selected{' '}
              {skippedDeleteCount === 1 ? 'session is' : 'sessions are'} not
              void and will be skipped.
            </Typography>
          )}
        </Stack>
      </DialogSimple>
    </>
  );
}
